import { ArrowUpRight } from "lucide-react";
import { mediaChannels } from "@/content/siteData";

const SpotifyIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
    <circle cx="12" cy="12" r="12" fill="#1DB954" />
    <path d="M17.35 15.3a.6.6 0 0 1-.82.2 8.9 8.9 0 0 0-8.15-1.03.6.6 0 1 1-.38-1.14 10.1 10.1 0 0 1 9.26 1.2.6.6 0 0 1 .12.77z" fill="#191414" />
    <path d="M18.1 12.95a.75.75 0 0 1-1.02.26 11.4 11.4 0 0 0-9.98-1.2.75.75 0 1 1-.49-1.42 12.9 12.9 0 0 1 11.27 1.38.75.75 0 0 1 .22.98z" fill="#191414" />
    <path d="M18.35 10.43a.9.9 0 0 1-1.23.31 14.3 14.3 0 0 0-11.6-1.55.9.9 0 1 1-.53-1.72 16.1 16.1 0 0 1 13.05 1.75.9.9 0 0 1 .31 1.21z" fill="#191414" />
  </svg>
);

const ApplePodcastsIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
    <circle cx="12" cy="12" r="12" fill="#A241FF" />
    <circle cx="12" cy="8" r="1.65" fill="#ffffff" />
    <path d="M12 10.45a4.4 4.4 0 0 0-2.8 7.8l.58-1.42a2.85 2.85 0 1 1 4.44 0l.58 1.42A4.4 4.4 0 0 0 12 10.45z" fill="#ffffff" />
    <path d="M13.03 13.86h-2.06l-.88 4.13h3.82l-.88-4.13z" fill="#ffffff" />
  </svg>
);

const YouTubeIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
    <rect x="1.5" y="5" width="21" height="14" rx="4.5" fill="#FF0000" />
    <path d="M10 9.2v5.6l4.95-2.8L10 9.2z" fill="#ffffff" />
  </svg>
);

const iconByChannel = {
  spotify: SpotifyIcon,
  "apple-podcasts": ApplePodcastsIcon,
  youtube: YouTubeIcon,
};

const styleByChannel = {
  spotify: {
    chip: "border-[#1DB954]/35 bg-white text-[#145a33] hover:border-[#1DB954]/60 hover:bg-[#1DB954]/10",
    arrow: "text-[#1DB954]",
  },
  "apple-podcasts": {
    chip: "border-[#A241FF]/35 bg-white text-[#5f2b91] hover:border-[#A241FF]/60 hover:bg-[#A241FF]/10",
    arrow: "text-[#A241FF]",
  },
  youtube: {
    chip: "border-[#FF0000]/35 bg-white text-[#8a0015] hover:border-[#FF0000]/60 hover:bg-[#FF0000]/10",
    arrow: "text-[#FF0000]",
  },
};

export const MediaChannelLinks = ({
  testIdPrefix = "media-channels",
  heading = "Listen & watch",
  className = "",
}) => {
  return (
    <div
      className={`rounded-2xl border border-border bg-white p-4 ${className}`.trim()}
      data-testid={`${testIdPrefix}-section`}
    >
      <p
        className="text-xs font-semibold uppercase tracking-wide text-primary/80"
        data-testid={`${testIdPrefix}-heading`}
      >
        {heading}
      </p>

      <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-list`}>
        {mediaChannels.map((channel) => {
          const Icon = iconByChannel[channel.id] ?? YouTubeIcon;
          const channelStyles =
            styleByChannel[channel.id] ?? {
              chip: "border-border bg-muted/20 text-foreground hover:border-primary/40 hover:bg-primary/10",
              arrow: "text-primary",
            };

          return (
            <a
              key={channel.id}
              href={channel.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors duration-300 ${channelStyles.chip}`}
              data-testid={`${testIdPrefix}-link-${channel.id}`}
            >
              <Icon className="h-4 w-4" />
              <span data-testid={`${testIdPrefix}-label-${channel.id}`}>{channel.label}</span>
              <ArrowUpRight className={`h-3.5 w-3.5 ${channelStyles.arrow}`} />
            </a>
          );
        })}
      </div>
    </div>
  );
};